<template>
  <component-wrapper
    v-if="showBuyBox"
    :class="{
      'buy-box-module': true,
      'buy-box-module--unavailable': !isAvailable,
      'buy-box-module--expired': isExpired,
    }"
  >
    <div
      v-if="isAppView"
      class="buy-box-module__snackbar"
    >
      <snackbar-fi top-position />
    </div>
    <error-message
      v-if="haveErrors"
      trigger-on-creation
      class="buy-box-module__error"
    >
      {{ $dict.get('online-Shop.Checkout.Errors.Title') }}
    </error-message>
    <div
      v-if="resultObject"
      class="buy-box-module__buy-box"
      data-test="buy-box"
    >
      <div
        v-if="!isSM"
        class="buy-box-module__buy-box--mobile"
      >
        <template v-if="!isExpired">
          <div class="buy-box-module__buy-box__col">
            <template v-if="!withoutPrices">
              <div
                v-if="ecoContributionFormatted"
                class="fi-flex fi-flex-col"
              >
                <div class="buy-box-module__eco">
                  {{ $dict.get('checkout.Cart.Sum') }}
                </div>
                <price-savings-fi
                  :class="priceSavingsClass"
                  :best-price="getBestPrice"
                  is-mobile
                />
              </div>
              <price-savings-fi
                v-else
                :class="priceSavingsClass"
                :best-price="getBestPrice"
                is-mobile
              />
            </template>

            <div
              v-if="isAvailable"
              class="buy-box-module__buy-box__quantity-steps"
            >
              <quantity-steps
                ref="quantitySteps"
                class="theme--blocked"
                template="article"
                :requested-quantity="quantity"
                :item="resultObject"
                :price-rows-savings="
                  resultPriceRowsSavings.length > 1 ? resultPriceRowsSavings : null
                "
                small
                light
                @blur="onBlur"
                @update-quantity="updateQuantity"
                @user-input="backgroundUpdateQuantity"
                @scroll-to-element="scrollToSavings"
                @value-rounded="onValueRounded"
              />

              <div class="buy-box-module__buy-box__quantity-per-package">
                {{ $dict.get('online-Shop.Product.Graduated-Prices.Quantity-Headline') }}
                <product-package-size-fi
                  class="fi-inline"
                  :item="resultObject"
                />
              </div>
              <product-partials-fi
                class="buy-box-module__allow-partials"
                :best-price="resultObject"
              />
            </div>
          </div>

          <div class="buy-box-module__buy-box__info">
            <availability-status
              class="buy-box-module__buy-box__availability"
              :delivery-speed="resultObject.deliverySpeed"
              :stock-levels="stockLevels"
              :disable-tooltip="true"
              theme="buy-box"
            />

            <div
              v-if="!withoutPrices"
              class="buy-box-module__buy-box__info__text"
            >
              <div class="buy-box-module__buy-box__vat">
                <template v-if="resultObject.taxRate !== 0">
                  {{ $dict.get('online-Shop.Product.Vat-excluded') }},
                </template>
                {{ $dict.get('online-Shop.Product.Plus-Shipping') }}
                {{ $dict.get('online-Shop.Product.Base-Price') }}:
              </div>

              <div class="buy-box-module__buy-box__basic-prices">
                <div class="buy-box-module__buy-box__basic-price">
                  <base-price-fi
                    :best-price="resultObject"
                    small
                  />
                </div>
              </div>
              <div
                v-if="ecoContributionFormatted"
                class="buy-box-module__eco"
              >
                {{ ecoContributionFormatted }}
              </div>
            </div>
          </div>

          <availability-status
            class="buy-box-module__buy-box__availability"
            :delivery-speed="resultObject.deliverySpeed"
            :stock-levels="stockLevels"
            :disable-tooltip="true"
            theme="buy-box"
          />
        </template>
        <div
          v-else
          class="buy-box-module__expired-info"
        >
          <span
            data-test="buy-box-expired"
            class="buy-box-module__expired-info__text"
            >{{ $dict.get('online-Shop.Product.Expired') }}</span
          >
        </div>

        <quantity-modification-hint-fi
          class="buy-box-module__quantity-modification-hint"
          :hint="quantityModificationHint"
          :data-test-2="quantityDataTest2"
          icon="info"
        />

        <button-fi
          v-if="isAvailable"
          ref="addProductMobile"
          type="button"
          class="button-fi__primary buy-box-module__action-button button__add-to-cart"
          data-test="add-to-cart"
          :disabled="isLoading || addToCartLoading"
          @click.prevent="addProduct"
        >
          {{ $dict.get('online-Shop.Product.Add-to-Cart') }}
        </button-fi>

        <button-fi
          v-if="useMemoList && !isExpired && !isAppView && isAvailable"
          ref="addMemoListMobile"
          type="button"
          class="button-fi__tertiary buy-box-module__action-button button__add-to-memolist"
          data-test="add-to-wish-list"
          @click="addToMemoList"
        >
          {{ $dict.get('online-Shop.Product.Add-to-Memo-List') }}
        </button-fi>
      </div>
      <div
        v-else
        class="buy-box-module__buy-box--desktop"
      >
        <div class="buy-box-module__buy-box__left">
          <loading-indicator
            v-if="(isLoading && !haveErrors) || addToCartLoading"
            variant="spinnerLocal"
            spinoff="buy-box-module__buy-box--left"
            dark
          />

          <template v-if="!isExpired">
            <template v-if="!withoutPrices">
              <div
                v-if="ecoContributionFormatted"
                class="buy-box-module__eco"
              >
                <span>{{ $dict.get('checkout.Cart.Sum') }}</span>
              </div>

              <price-savings-fi
                :class="priceSavingsClass"
                :best-price="getBestPrice"
              />
            </template>

            <div class="buy-box-module__buy-box__info">
              <availability-status
                class="buy-box-module__buy-box__availability"
                :delivery-speed="resultObject.deliverySpeed"
                :stock-levels="stockLevels"
                :disable-tooltip="true"
                theme="buy-box"
              />

              <div
                v-if="!withoutPrices"
                class="buy-box-module__buy-box__info__text"
              >
                <div class="buy-box-module__buy-box__vat">
                  <template v-if="resultObject.taxRate !== 0">
                    {{ $dict.get('online-Shop.Product.Vat-excluded') }},
                  </template>
                  {{ $dict.get('online-Shop.Product.Plus-Shipping') }}
                </div>

                <div class="buy-box-module__buy-box__basic-prices">
                  <div class="buy-box-module__buy-box__basic-prices__label">
                    {{ $dict.get('online-Shop.Product.Base-Price') }}:
                  </div>

                  <div class="buy-box-module__buy-box__basic-price">
                    <base-price-fi :best-price="resultObject" />
                  </div>

                  <div
                    v-if="ecoContributionFormatted"
                    class="buy-box-module__eco"
                  >
                    <span>{{ ecoContributionFormatted }}</span>
                  </div>
                </div>
              </div>
            </div>

            <availability-status
              class="buy-box-module__buy-box__availability"
              :delivery-speed="resultObject.deliverySpeed"
              :stock-levels="stockLevels"
              :disable-tooltip="true"
              theme="buy-box"
            />
          </template>

          <div
            v-else
            class="buy-box-module__expired-info"
          >
            <span
              data-test="buy-box-expired"
              class="buy-box-module__expired-info__text"
              >{{ $dict.get('online-Shop.Product.Expired') }}</span
            >
          </div>
        </div>

        <div
          v-if="!isExpired"
          class="buy-box-module__buy-box__right"
        >
          <quantity-steps
            v-if="isAvailable"
            ref="quantitySteps"
            class="theme--blocked"
            template="article"
            :requested-quantity="quantity"
            :item="resultObject"
            :price-rows-savings="resultPriceRowsSavings.length > 1 ? resultPriceRowsSavings : null"
            light
            large
            @blur="onBlur"
            @update-quantity="updateQuantity"
            @user-input="backgroundUpdateQuantity"
            @scroll-to-element="scrollToSavings"
            @value-rounded="onValueRounded"
          />

          <div
            v-if="isAvailable"
            class="buy-box-module__buy-box__quantity-per-package"
          >
            {{ $dict.get('online-Shop.Product.Graduated-Prices.Quantity-Headline') }}
            <product-package-size-fi
              class="fi-inline"
              :item="resultObject"
            />
          </div>

          <product-partials-fi
            v-if="isAvailable"
            class="buy-box-module__allow-partials"
            :best-price="resultObject"
          />

          <quantity-modification-hint-fi
            class="buy-box-module__quantity-modification-hint"
            :hint="quantityModificationHint"
            :data-test-2="quantityDataTest2"
            icon="info"
          />

          <button-fi
            v-if="isAvailable"
            ref="addProductDesktop"
            type="button"
            class="buy-box-module__buy-box__add-to-cart button-fi__primary buy-box-module__action-button button__add-to-cart button-fi--large"
            data-test="add-to-cart"
            :data-test-2="Boolean(isLoading || addToCartLoading) ? 'add-to-cart-disabled' : null"
            :disabled="Boolean(isLoading || addToCartLoading)"
            @click.prevent="addProduct"
          >
            {{ $dict.get('online-Shop.Product.Add-to-Cart') }}
          </button-fi>

          <button-fi
            v-if="useMemoList && !isAppView && isAvailable"
            ref="addMemoListDesktop"
            type="button"
            class="button-fi__tertiary buy-box-module__action-button button__add-to-memolist button-fi--large"
            data-test="add-to-wish-list"
            @click="addToMemoList"
          >
            {{ $dict.get('online-Shop.Product.Add-to-Memo-List') }}
          </button-fi>
        </div>
      </div>
    </div>

    <div
      v-if="!haveBestPrice"
      class="buy-box-module__buy-box buy-box-module__buy-box__expired"
    >
      <span
        class="buy-box-module__buy-box__expired__text"
        data-test="buy-box-expired"
        >{{ $dict.get('online-Shop.Product.Expired') }}</span
      >
    </div>

    <teleport
      v-if="
        isMounted &&
        productDetailHeadMounted &&
        resultPriceRowsSavings &&
        resultPriceRowsSavings.length > 1 &&
        !isExpired &&
        !isMD
      "
      to="#price-row-savings"
    >
      <div class="price-rows-savings--mobile">
        <price-rows-savings-fi
          ref="savings"
          mobile
          :savings="resultPriceRowsSavings"
        />
      </div>
    </teleport>

    <add-to-memo-list-dialog-fi
      v-if="!isAppView"
      ref="addToMemoListDialog"
      :product="productCode"
      :quantity="quantity"
      multiple
      @error-occured="enterErrorState"
      @added-to-memolist="$emit('added-to-memolist')"
    />
  </component-wrapper>
</template>

<script>
import {
  AvailabilityStatus,
  BasePriceFi,
  ButtonFi,
  ErrorMessage,
  LoadingIndicator,
  PriceSavingsFi,
  ProductPackageSizeFi,
  ProductPartialsFi,
  QuantityModificationHintFi,
} from 'atoms';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import AddToMemoListDialogFi from 'components/memo-list/add-to-memo-list-dialog-fi/AddToMemoListDialogFi';
import PriceRowsSavingsFi from 'components/price-rows-savings-fi/PriceRowsSavingsFi';
import getEcoContribution from 'components/shop-modules/eco-contribution/getEcoContribution';
import QuantitySteps from 'components/shop-modules/quantity-steps/QuantitySteps';
import SnackbarFi from 'components/shop-modules/snackbar-fi/SnackbarFi';
import { useMemoListFunction } from 'composables/useMemoList';
import breakpointMixin from 'mixins/breakpointMixin';
import sellingUnitMixin from 'mixins/sellingUnitMixin';
import shoppingcartMixin from 'mixins/shoppingcartMixin';
import userMixin from 'mixins/userMixin';
import { mapGetters } from 'vuex';

const importGETBESTPRICE = () => import('gql/getBestPrice.gql');

export default {
  name: 'BuyBoxModule',
  components: {
    AddToMemoListDialogFi,
    AvailabilityStatus,
    ButtonFi,
    ComponentWrapper,
    ErrorMessage,
    LoadingIndicator,
    PriceRowsSavingsFi,
    ProductPackageSizeFi,
    ProductPartialsFi,
    QuantityModificationHintFi,
    QuantitySteps,
    SnackbarFi,
    BasePriceFi,
    PriceSavingsFi,
  },
  mixins: [breakpointMixin, shoppingcartMixin, sellingUnitMixin, userMixin],
  emits: ['added-to-memolist'],
  data() {
    return {
      quantity: 0,
      initialQuantity: null,
      initialPrice: null,
      shippingDialog: false,
      haveErrors: false,
      addToCartLoading: false,
      getBestPrice: null,
      haveBestPrice: true,
      quantityModificationHint: '',
      quantityDataTest2: null,
      current: true,
      GETBESTPRICE: null,
      isMounted: false,
    };
  },
  computed: {
    ...mapGetters({
      productDetailHeadMounted: 'layout/getProductDetailHeadMounted',
    }),
    useMemoList() {
      return useMemoListFunction(this.$fischer);
    },
    priceSavingsClass() {
      return {
        'buy-box-module__buy-box__price-saving': true,
        'buy-box-module__buy-box__price-saving--outdated': !this.current,
      };
    },
    bestpriceVariables() {
      return {
        baseSiteId: this.$fischer.baseSiteId(),
        accessToken: this.accessToken,
        productCode: this.productCode,
        qty: this.quantity !== this.initialQuantity ? this.quantity : 0, // this will avoid requesting the price twice on initial load
        language: this.$fischer.siteLanguage(),
        expired: true,
        userId: this.$fischer.user.id(),
      };
    },
    ecoContributionFormatted() {
      if (!this.$fischer.shop.useEco()) {
        return null;
      }
      const basePrice = getEcoContribution(this.getBestPrice)?.price?.basePrices?.[0];
      if (!basePrice) {
        return null;
      }
      const contribution = this.$dict.get('online-Shop.Product.Product-Eco', [
        basePrice.qty,
        basePrice.unit,
        basePrice.valueFormatted,
      ]);
      return this.getSellingUnitInString(contribution);
    },
    stockLevels() {
      return this.resultObject?.stockLevels || null;
    },
    showBuyBox() {
      return Boolean(
        (this.$fischer.shop.isClosedShop() || this.$fischer.shop.isOnlineShop()) &&
          this.accessToken &&
          this.productCode,
      );
    },
    accessToken() {
      return this.$fischer.user.accessToken();
    },
    isLoading() {
      return this.$apolloData?.loading;
    },
    productCode() {
      if (this.$fischer.query('article')) {
        return this.$fischer.query('article');
      }

      return this.$fischer.catalogId('040390');
    },
    resultObject() {
      return this.getBestPrice;
    },
    resultPriceRowsSavings() {
      if (this.resultObject) {
        let activeIndex = 0;
        const savings = JSON.parse(
          JSON.stringify([
            {
              ...this.initialPrice,
              minQty: this.resultObject.initialQuantity,
              priceQty: this.resultObject.initialQuantity,
              savingsInformation: null,
            },
            ...(this.resultObject.priceRowsIncludingSavings || []),
          ]),
        );

        savings.forEach((saving, index) => {
          saving.active = false;

          if (saving.minQty <= this.quantity) {
            activeIndex = index;
          }
        });

        savings[activeIndex].active = true;

        return savings;
      }

      return null;
    },
    isAvailable() {
      return this.resultObject?.deliverySpeed && this.resultObject.deliverySpeed !== 'NODELIVERY';
    },
    isExpired() {
      return this.resultObject?.articleStatus && this.resultObject.articleStatus === 'EXPIRED';
    },
    withoutPrices() {
      return this.$fischer.shop.hasPurchasePermissionWithoutPrices();
    },
  },
  watch: {
    getBestPrice(getBestPrice) {
      if (this.initialQuantity || this.initialPrice) {
        return;
      }
      if (this.canOrderItem(getBestPrice)) {
        this.initialQuantity = getBestPrice.initialQuantity;
        this.initialPrice = getBestPrice.price;
        this.quantity = getBestPrice.requestedQuantity || this.initialQuantity;
      }
    },
  },
  async mounted() {
    this.GETBESTPRICE = (await importGETBESTPRICE()).default;
    this.isMounted = true;
  },
  apollo: {
    getBestPrice: {
      query() {
        return this.GETBESTPRICE;
      },
      errorPolicy: 'all',
      variables() {
        return this.bestpriceVariables;
      },
      skip() {
        return (
          !this.GETBESTPRICE ||
          !this.productCode ||
          (this.initialQuantity === this.quantity && this.getBestPrice?.requestedQuantity === 0)
        );
      },
      async error(error) {
        await this.logoutIfTokenInvalid(error);
        this.haveBestPrice = false;
        this.enterErrorState('error getting best price', error);
      },
      result({ data }) {
        this.getBestPrice = data?.getBestPrice;

        if (!this.getBestPrice?.price) {
          this.haveBestPrice = false;
        }
      },
    },
  },
  methods: {
    onValueRounded(hint, dataTest2) {
      this.quantityModificationHint = hint;
      this.quantityDataTest2 = dataTest2;
    },
    onBlur(value, evt) {
      // the blur cancels the click on any button... usually... performs the button's action here
      const target = evt?.explicitOriginalTarget;
      if (!target) {
        return;
      }
      if (
        target === this.$refs.addProductDesktop?.$el ||
        target.parentNode === this.$refs.addProductDesktop?.$el ||
        target === this.$refs.addProductMobile?.$el ||
        target.parentNode === this.$refs.addProductMobile?.$el
      ) {
        this.addProduct();
      }
      if (
        target === this.$refs.addMemoListDesktop?.$el ||
        target.parentNode === this.$refs.addMemoListDesktop?.$el ||
        target === this.$refs.addMemoListMobile?.$el ||
        target.parentNode === this.$refs.addMemoListMobile?.$el
      ) {
        this.addToMemoList();
      }
    },
    addToMemoList() {
      this.quantityModificationHint = '';
      this.$refs.addToMemoListDialog.show();
    },
    canOrderItem(bestPrice) {
      return bestPrice?.quantityStepRanges?.length && bestPrice.quantityStepRanges[0].rangeValue;
    },
    updateQuantity(value) {
      this.quantity = parseInt(value, 10);
      this.quantityModificationHint = '';
      this.current = true;
    },
    async backgroundUpdateQuantity(valueRaw) {
      if (!valueRaw) {
        this.current = false;
        return;
      }
      const value = Number.parseInt(valueRaw);
      if (this.lastInputValue === value || this.quantity === value) {
        return;
      }
      this.lastInputValue = value;

      const initial = this.getBestPrice?.initialQuantity;
      if (!initial || value < initial) {
        return;
      }

      try {
        const query = (await importGETBESTPRICE()).default;
        const backgroundBestPrice = await this.$apollo.query({
          query,
          errorPolicy: 'all',
          variables: {
            ...this.bestpriceVariables,
            qty: value,
          },
        });
        if (backgroundBestPrice.data?.getBestPrice?.requestedQuantity === this.lastInputValue) {
          this.updateQuantity(value);
        }
      } catch (error) {
        // noop
      }
    },
    enterErrorState(...errors) {
      this.haveErrors = true;
      console.error(...errors);
    },
    addProduct() {
      if (this.addToCartLoading) {
        return;
      }
      this.addToCartLoading = true;
      if (
        this.quantity !==
        Number(this.$refs.quantitySteps.getRawValue(this.$refs.quantitySteps.quantityInputValue))
      ) {
        this.$refs.quantitySteps.triggerInput();
      }
      this.addProductToCart({
        productID: this.productCode,
        productRequestedQuantity: this.quantity,
        productInitialQuantity: this.resultObject.initialQuantity,
      }).then((res) => {
        if (res?.success) {
          this.addToCartLoading = false;
        }
      });
    },
    scrollToSavings() {
      if (IS_SSR) {
        return;
      }

      const savings = document.querySelectorAll('.sticky-actions-intersector');
      if (savings) {
        this.$smoothScroll({
          scrollTo: savings.length > 1 ? savings[1] : savings[0],
          duration: 500,
          updateHistory: false,
          offset: -120,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './buy-box-module';
</style>

<style lang="scss">
@import './buy-box-module-unscoped';
</style>
